import './customer-account-refresh.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { request } from '../lib/api';
import { formatMoney } from '../lib/money';
import {
  billingPortalRequestSchema,
  billingPortalResponseSchema,
  checkoutSessionResponseSchema,
  customerPlanCatalogResponseSchema,
  planCheckoutRequestSchema,
  type CustomerPlanCatalogItem,
  type PaidPlan
} from '@inktap/contracts/customer-commerce';
import { formatPlan } from './portalTypes';
import { useDashboardSummary } from './useDashboardSummary';
import BillingStorageOverview from './BillingStorageOverview';

const UPGRADE_OPTIONS: Array<{
  plan: PaidPlan;
  label: string;
  outcome: string;
  benefits: string[];
}> = [
  {
    plan: 'basic',
    label: 'Basic',
    outcome: 'Keep your card and tap page in sync with a practical everyday workspace.',
    benefits: ['Cloud-synced card designs', '100 MB for card and page assets', 'Four tap-page actions']
  },
  {
    plan: 'boost',
    label: 'Boost',
    outcome: 'Share more easily and understand what is working.',
    benefits: ['A memorable public tap link', 'Richer tap and action analytics', 'Downloadable card proofs']
  },
  {
    plan: 'secure',
    label: 'Secure',
    outcome: 'Control who can open privacy-sensitive pages.',
    benefits: ['Lost Card Protection', 'Private-page access controls', 'Downloadable card proofs']
  },
  {
    plan: 'premium',
    label: 'Premium',
    outcome: 'Combine growth tools, privacy, and priority support.',
    benefits: ['Lost Card Protection', 'Public sharing and advanced analytics', 'Priority support when you need help']
  }
];

function PlanBillingPage() {
  const location = useLocation();
  const { summary, loading, error, reload } = useDashboardSummary();
  const [checkoutPlan, setCheckoutPlan] = useState<PaidPlan | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<CustomerPlanCatalogItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const catalogRequest = useRef(0);
  const plansHeadingRef = useRef<HTMLHeadingElement>(null);
  const hasManageableBilling = Boolean(summary?.plan.plan && summary.plan.plan.toLowerCase() !== 'free');
  const catalogByPlan = new Map(catalog.map((item) => [item.plan.toLowerCase(), item]));
  const entitlements = summary?.plan.entitlements;
  const includedCapabilities = entitlements ? [
    entitlements.canSyncToCloud && 'Cloud-synced designs',
    entitlements.canManagePublicAlias && 'Public Tap Page alias',
    entitlements.canControlIndexability && 'Search indexing controls',
    entitlements.canUseBookingEmbeds && 'Booking embeds',
    entitlements.canUseAnalytics && 'Tap Page analytics',
    entitlements.canUseProtectedResources && 'Protected resources',
    entitlements.canManagePhysicalCards && 'Physical card controls',
    entitlements.canDownloadProof && 'Downloadable proofs'
  ].filter((item): item is string => Boolean(item)) : null;

  const refreshCatalog = useCallback(async () => {
    const version = ++catalogRequest.current;
    setCatalogLoading(true);
    try {
      const response = await request<unknown>('/billing/plan-catalog', { cache: 'no-store' });
      const data = customerPlanCatalogResponseSchema.safeParse(response);
      if (!data.success) throw new Error('Unable to confirm current pricing.');
      if (version !== catalogRequest.current) return null;
      setCatalog(data.data.items);
      return data.data.items;
    } catch {
      // Never keep a previous numeric offer after a failed freshness check.
      if (version === catalogRequest.current) setCatalog([]);
      return null;
    } finally {
      if (version === catalogRequest.current) setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    const refresh = () => { void refreshCatalog(); };
    refresh();
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    return () => {
      ++catalogRequest.current;
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pageshow', refresh);
    };
  }, [location.key, refreshCatalog]);

  const handleCheckout = useCallback(async (plan: PaidPlan) => {
    setCheckoutPlan(plan);
    setCheckoutError(null);
    try {
      const shown = catalog.find((item) => item.plan.toLowerCase() === plan);
      const latest = await refreshCatalog();
      const current = latest?.find((item) => item.plan.toLowerCase() === plan);
      if (!current || !shown) throw new Error('Unable to confirm current pricing. Please reload and try again.');
      if (current.changeKind !== 'upgrade') throw new Error('Your available plan changes have updated. Review the plans before continuing.');
      if (
        current.pricingToken !== shown.pricingToken || current.unitAmount !== shown.unitAmount ||
        current.currency !== shown.currency || current.interval !== shown.interval || current.intervalCount !== shown.intervalCount
      ) {
        throw new Error('Pricing has changed. Review the refreshed price, then choose your plan again.');
      }
      const body = planCheckoutRequestSchema.safeParse({
        plan, ...(current.pricingToken ? { pricingToken: current.pricingToken } : {})
      });
      if (!body.success) throw new Error('Unable to confirm current pricing. Please reload and try again.');
      const response = await request<unknown>('/billing/plan-checkout', {
        method: 'POST',
        body: JSON.stringify(body.data)
      });
      const data = checkoutSessionResponseSchema.safeParse(response);
      if (!data.success) throw new Error('Unable to confirm checkout. Reload before trying again.');
      window.location.href = data.data.url;
    } catch (err: any) {
      if (err?.status === 409 && err?.data?.code === 'PRICE_CHANGED') await refreshCatalog();
      setCheckoutError(err?.status === 409 && err?.data?.code === 'PRICE_CHANGED'
        ? 'Pricing has changed. Review the refreshed price, then choose your plan again.'
        : err?.data?.message ?? err?.message ?? 'Unable to start checkout.');
      setCheckoutPlan(null);
    }
  }, [catalog, refreshCatalog]);

  const handleBillingPortal = useCallback(async () => {
    setPortalLoading(true);
    setCheckoutError(null);
    try {
      const body = billingPortalRequestSchema.safeParse({ returnUrl: window.location.href });
      if (!body.success) throw new Error('Unable to open billing portal.');
      const response = await request<unknown>('/billing/portal', {
        method: 'POST',
        body: JSON.stringify(body.data)
      });
      const data = billingPortalResponseSchema.safeParse(response);
      if (!data.success) throw new Error('Unable to confirm billing portal. Reload before trying again.');
      window.location.href = data.data.url;
    } catch (err: any) {
      setCheckoutError(err?.data?.message ?? err?.message ?? 'Unable to open billing portal.');
      setPortalLoading(false);
    }
  }, []);

  const storageSection = <BillingStorageOverview />;

  return (
    <div className="dashboard-page portal-page portal-workspace billing-overview">
      <header className="portal-intro">
        <h1 tabIndex={-1}>Plan &amp; billing</h1>
        <p>Choose the plan that helps you share, measure, and protect your tap page.</p>
      </header>

      {error ? <div className="overview-recovery"><div className="alert" role="alert">{error}</div><button type="button" className="button-secondary" onClick={(event) => { event.currentTarget.closest('.portal-workspace')?.querySelector('h1')?.focus(); void reload(); }}>Retry account summary</button></div> : null}
      {checkoutError ? <div className="alert" role="alert">{checkoutError}</div> : null}

      {!summary && !loading ? storageSection : null}

      {loading ? (
        <section className="dashboard-section" aria-live="polite">
          <p className="helper-text">Loading plan details...</p>
        </section>
      ) : summary ? (
        <>
          <section className="current-plan-band">
            <div>
              <span>Your current plan</span>
              <h2>{formatPlan(summary.plan.plan)}</h2>
              <p>Review your workspace plan and the available options below.</p>
              <p>Plan prices below describe current offers. Your billing portal confirms existing subscription charges and credits.</p>
              <div className="billing-current-actions">
                <button type="button" className="button-primary" onClick={() => plansHeadingRef.current?.focus()}>Compare plans</button>
                {hasManageableBilling ? (
                  <button type="button" className="button-secondary" onClick={() => void handleBillingPortal()} disabled={portalLoading}>
                    {portalLoading ? 'Opening...' : 'Manage billing'}
                  </button>
                ) : null}
              </div>
            </div>
            <div className="billing-current-next">
              {includedCapabilities ? (
                <div className="billing-current-includes">
                  <h3>Included capabilities</h3>
                  {includedCapabilities.length ? (
                    <ul aria-label="Included capabilities">{includedCapabilities.map((item) => <li key={item}>{item}</li>)}</ul>
                  ) : <p>No optional capabilities are reported for this workspace.</p>}
                </div>
              ) : null}
              <h3>Billing and payments</h3>
              <p>{hasManageableBilling
                ? 'Use Manage billing to review your subscription and available payment details.'
                : 'Review the plans below to see available subscription options.'}</p>
            </div>
          </section>

          {storageSection}

          <section className="billing-plan-section" aria-labelledby="available-plans-heading">
            <div className="billing-plan-section__heading">
              <h2 id="available-plans-heading" ref={plansHeadingRef} tabIndex={-1}>Compare plans</h2>
              <p>Review available options and current offers before continuing.</p>
            </div>
            <div className="plan-comparison">
            {UPGRADE_OPTIONS.map((option) => {
              const isCurrentPlan = summary.plan.plan?.toLowerCase() === option.plan;
              const pricing = catalogByPlan.get(option.plan);
              const changeKind = pricing?.changeKind ?? (isCurrentPlan ? 'current' : null);
              const usesBillingPortal = changeKind === 'switch' || changeKind === 'downgrade';
              const hasPrice = !catalogLoading && pricing?.pricingToken && pricing.currency &&
                pricing.unitAmount !== null && pricing.interval && pricing.intervalCount;
              const offerPrice = hasPrice ? formatMoney(pricing.unitAmount!, pricing.currency!) : null;
              const intervalLabel = hasPrice
                ? pricing.intervalCount === 1 ? `/${pricing.interval}` : ` every ${pricing.intervalCount} ${pricing.interval}s`
                : '';
              return (
                <article key={option.plan} className="plan-option" data-promoted={pricing?.promotion ? 'true' : undefined}>
                  <div>
                    <h2>{pricing?.label ?? option.label}</h2>
                    <p className="plan-option__outcome">{pricing?.description ?? option.outcome}</p>
                  </div>
                  {pricing && !catalogLoading ? (
                    <div className="plan-option__pricing">
                      <strong>{hasPrice ? `${offerPrice}${intervalLabel}` : 'Price confirmed at checkout'}</strong>
                      {pricing.promotion ? <span>{pricing.promotion.publicLabel}</span> : null}
                      {pricing.promotion ? <small>Offer eligibility and final terms are confirmed at checkout.</small> : null}
                    </div>
                  ) : <div className="plan-option__pricing plan-option__pricing--loading">{catalogLoading ? 'Loading price…' : 'Price confirmed at checkout'}</div>}
                  <ul>
                    {option.benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}
                  </ul>
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() => void (usesBillingPortal ? handleBillingPortal() : handleCheckout(option.plan))}
                    disabled={catalogLoading || checkoutPlan !== null || portalLoading || changeKind === null || changeKind === 'current'}
                  >
                    {changeKind === null
                      ? 'Temporarily unavailable'
                      : changeKind === 'current'
                      ? 'Current plan'
                      : checkoutPlan === option.plan || (usesBillingPortal && portalLoading)
                        ? 'Opening...'
                        : changeKind === 'switch'
                          ? `Switch to ${option.label}`
                          : changeKind === 'downgrade'
                            ? `Change to ${option.label}`
                            : `Upgrade to ${option.label}`}
                  </button>
                </article>
              );
            })}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

export default PlanBillingPage;
